import type { ReactNode } from 'react'
import { createContext, use } from 'react'
import { getCurrentJoyfulApp, setCurrentJoyfulApp } from './appRuntimeStore'

import type { components } from '@joyful/openapi/types.gen'
import type { JoyfulApiClient } from './api'

export type JoyfulAppEnvironment = 'development' | 'staging' | 'production'

export type NavbarApp = 'app' | 'bpo' | 'internal' | 'workspace'

export type BrowserErrorApp = components['schemas']['BrowserErrorEvent']['app']

export interface JoyfulAppUrls {
  apiBaseUrl: string
  appBaseUrl?: string
  /**
   * Per-app origin overrides for cross-app navbar links (local worktree ports,
   * staging, or production). Apps read these from
   * `import.meta.env.VITE_JOYFUL_*_WEB_ORIGIN` at bootstrap and pass them in —
   * the shared UI package does not read `import.meta.env`.
   */
  navbarAppOrigins?: Partial<Record<NavbarApp, string>>
  webAppUrl?: string
}

export interface JoyfulAppDatadogConfig {
  appSurface?: NavbarApp
  applicationId: string
  clientToken: string
  /**
   * Chrome extension popups reload the DOM (and lose in-memory state) every
   * time they close, and third-party cookie restrictions can prevent the SDK
   * from persisting a session via cookies alone. Extensions should set this
   * so sessions fall back to `localStorage`.
   */
  isBrowserExtension?: boolean
  service?: string
  site?: string
  /** No React Router instance exists outside the web apps. */
  usesReactRouter?: boolean
  version?: string
}

export interface JoyfulAppTelemetryConfig {
  app: BrowserErrorApp
  datadog?: JoyfulAppDatadogConfig
  release?: string
}

export interface JoyfulAppRuntime {
  api: JoyfulApiClient
  appSurface?: NavbarApp
  appUrls: JoyfulAppUrls
  environment: JoyfulAppEnvironment
  telemetry?: JoyfulAppTelemetryConfig
}

export interface JoyfulAppProviderProps {
  app: JoyfulAppRuntime
  children: ReactNode
}

const JoyfulAppContext = createContext<JoyfulAppRuntime | null>(null)

export function JoyfulAppProvider({ app, children }: JoyfulAppProviderProps) {
  setCurrentJoyfulApp(app)
  return <JoyfulAppContext value={app}>{children}</JoyfulAppContext>
}

export function getJoyfulAppRuntime() {
  return getCurrentJoyfulApp<JoyfulAppRuntime>()
}

export function useJoyfulApp() {
  const app = use(JoyfulAppContext)
  if (app === null) {
    throw new Error('useJoyfulApp must be used within JoyfulAppProvider')
  }

  return app
}
